import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@app/../environments/environment';
import { jsonheader } from '@app/shared/constants';
import { businessDayOf } from '@app/shared/time';
import { frDay } from '@app/sales/rental-billing/rental-days';

// Matériel dont l'ordonnance manque ou expire avant `until` (sales/src/rental/prescription_renewal.rs) ; jours en instants à midi heure de Paris.
export interface RenewalLine {
  order_id: number;
  line_id: number;
  product_name: string;
  terms_name: string | null; // intitulé du contrat de location : la désignation montrée, l'article en infobulle
  interrupted: boolean;
  covered_until: string | null;
  expired_on: string | null;
  next_start: string | null;
}

// Un patient, tous dossiers confondus : une lettre. Ville = adresse de livraison du dossier le plus récent, où part la lettre.
export interface RenewalPatient {
  id: number;
  name: string | null;
  firstname: string | null;
  zip: string | null;
  city: string | null;
  lines: RenewalLine[];
}

export interface RenewalList {
  today: string;
  until: string;
  patients: RenewalPatient[];
}

// État de l'ordonnance d'un matériel, en clair, dans les mots de la lettre.
export function coverageLabel(l: RenewalLine): string {
  const fr = (iso: string | null) => frDay(businessDayOf(iso));
  const state = l.covered_until ? `Valable jusqu'au ${fr(l.covered_until)}` : l.expired_on ? `Expirée le ${fr(l.expired_on)}` : 'Aucune ordonnance';
  return l.next_start ? `${state} · suivante à partir du ${fr(l.next_start)}` : state;
}

@Injectable({ providedIn: 'root' })
export class RentalPrescriptionRenewalService {
  private http = inject(HttpClient);
  private url = environment.server_url + '/sales/rental_prescription_renewals';

  // `until` (instant à midi, dayNoonIso) absent : le serveur prend aujourd'hui + prescription_renewal_days de c_values 34.
  list$(until?: string | null): Observable<RenewalList> {
    return this.http.get<RenewalList>(this.url, until ? { params: { until } } : {});
  }

  // Même route pour le lot de l'écran et pour un patient seul (page du dossier) : une lettre par patient, fusionnées en un PDF.
  pdf$(patients: number[], until?: string | null): Observable<Blob> {
    return this.http.post(this.url + '/pdf', { patients, until: until ?? null }, { headers: jsonheader, responseType: 'blob' });
  }
}

// Le refus du serveur est un texte, rendu dans un Blob par responseType 'blob' : le relire avant de l'afficher.
export function blobErrorText(e: any): Promise<string> {
  return e?.error instanceof Blob ? e.error.text().catch(() => '') : Promise.resolve(typeof e?.error === 'string' ? e.error : '');
}
